import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Drumstick, Loader2 } from "lucide-react";
import { Mascot } from "./Mascot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const platformOptions = ["TikTok", "Instagram", "Facebook", "YouTube", "Other"];

const inputCls =
  "w-full rounded-2xl border border-[#E5DFD7] bg-white px-4 py-3 text-sm text-[#141915] placeholder:text-[#7B887D]/70 outline-none transition-colors focus:border-[#22C55E] focus:ring-2 focus:ring-[#BBE7C6]";

export const FeedTheLizard = () => {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [platform, setPlatform] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setError("");
    setStatus("sending");
    try {
      const res = await fetch(`${API}/submissions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), url: url.trim(), platform, website: honeypot }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          res.status === 429
            ? "Whoa, slow down — the lizard is full. Try again in a few minutes."
            : data.detail || "Something went wrong. Please check the fields and try again."
        );
      }
      setStatus("success");
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      setStatus("idle");
    }
  };

  const reset = () => {
    setName("");
    setUrl("");
    setPlatform("");
    setStatus("idle");
    setError("");
  };

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 sm:pt-12" data-testid="feed-the-lizard-section">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="rounded-3xl border border-[#BBE7C6] bg-[#EBF7EE] p-5 sm:p-7"
      >
        <div className="flex items-center gap-3">
          <Mascot className="w-11 h-11 ring-2 ring-[#BBE7C6] bg-white shrink-0" />
          <div>
            <h2 className="font-mono text-sm font-bold uppercase tracking-[0.18em] text-[#14532D] flex items-center gap-2">
              <Drumstick className="w-4 h-4 text-[#15803D]" strokeWidth={2.2} />
              Feed the Lizard
            </h2>
            <p className="mt-1 text-sm text-[#525E54] leading-snug">
              Found something suspicious? Send it our way and help decide what ScamLizard investigates next.
            </p>
          </div>
        </div>

        {status === "success" ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 rounded-2xl border border-[#BBE7C6] bg-white px-5 py-6 text-center"
            data-testid="feed-success-message"
          >
            <Mascot className="w-14 h-14 mx-auto ring-2 ring-[#BBE7C6] bg-[#EBF7EE]" />
            <p className="mt-3 font-display text-xl font-bold text-[#141915]">MM! MMMM! We got it.</p>
            <p className="mt-1.5 text-sm text-[#525E54]">
              Thanks for the tip. This offer has been added to the investigation queue.
            </p>
            <button
              onClick={reset}
              data-testid="feed-another-button"
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#2D372E] bg-white px-4 py-2 text-xs font-semibold text-[#141915] transition-colors hover:bg-[#141915] hover:text-white active:scale-95"
            >
              Feed another tip
            </button>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="mt-5 flex flex-col gap-3" data-testid="feed-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Offer / Company Name"
                required
                minLength={2}
                maxLength={80}
                className={inputCls}
                data-testid="feed-name-input"
                aria-label="Offer or Company Name"
              />
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="URL (e.g. suspicious-offer.com)"
                required
                maxLength={300}
                className={inputCls}
                data-testid="feed-url-input"
                aria-label="URL"
              />
            </div>

            <div>
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#14532D] mb-2">
                Where did you see it?
              </p>
              <div className="flex flex-wrap gap-1.5">
                {platformOptions.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPlatform(p)}
                    data-testid={`feed-platform-${p.toLowerCase()}`}
                    aria-pressed={platform === p}
                    className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all duration-200 active:scale-95 ${
                      platform === p
                        ? "border-[#14532D] bg-[#14532D] text-white"
                        : "border-[#BBE7C6] bg-white text-[#14532D] hover:border-[#15803D]"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <input
              type="text"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="absolute left-[-9999px] top-[-9999px] h-0 w-0 opacity-0"
              data-testid="feed-honeypot"
            />

            {error && (
              <p className="text-xs font-medium text-[#9F1239]" data-testid="feed-error-message">
                {error}
              </p>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                type="submit"
                disabled={status === "sending" || !platform}
                data-testid="feed-submit-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#141915] px-6 py-3 text-sm font-bold uppercase tracking-wide text-white transition-all duration-300 hover:bg-[#15803D] hover:gap-3 active:scale-95 disabled:opacity-50 disabled:pointer-events-none"
              >
                {status === "sending" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Feeding...
                  </>
                ) : (
                  <>
                    Feed the Lizard
                    <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                  </>
                )}
              </button>
              <p className="text-[11px] text-[#525E54]/80 leading-snug">
                No account required. Don't include passwords or personal information.
              </p>
            </div>
          </form>
        )}
      </motion.div>
    </section>
  );
};
